import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import { setContext } from 'apollo-link-context'
import cookies from 'js-cookie'
import createApolloCache from './apollo-cache'
import { captureException } from './sentry'
import getConfig from 'next/config'
import { capitalize } from '~/utils/capitalize'
import getLocaleFromHostname from '~/utils/i18n/getLocaleFromHostname'

const { API_URL, TOKEN_COOKIE_NAME } = getConfig().publicRuntimeConfig
const { SECRET_INTERNAL_API_URL } = getConfig().serverRuntimeConfig
const dev = process.env.NODE_ENV !== 'production'
const isBrowser = typeof window !== 'undefined'
const APOLLO_URI = isBrowser ? API_URL : SECRET_INTERNAL_API_URL

// These errors are already handled in the frontend
const filterErrors = ({ message }) => !['Invalid or expired login link'].includes(message)

export default function createApolloClient(initialState, ctx) {
  // The `ctx` (NextPageContext) will only be present on the server.
  // use it to extract auth headers (ctx.req) or similar.

  const { getToken, getLanguage, getForwardedHeaders, getPath } = ctx
  const page = capitalize(getPath().split('?')[0].split('/')[1] || 'home').replace(/-/g, ' ')
  const path = encodeURIComponent(getPath())
  const httpLink = new BatchHttpLink({
    uri: isBrowser
      ? `${APOLLO_URI}/graphql/public/batch`
      : `${APOLLO_URI}/graphql/public/batch?page=${page}&path=${path}`,
    credentials: 'same-origin',
  })

  // Error reporting link
  const errorHandler = onError(({ graphQLErrors, networkError, operation, response }) => {
    if (graphQLErrors) {
      graphQLErrors.filter(filterErrors).forEach(graphqlError =>
        captureException(new Error(graphqlError.message), 'graphql', {
          extra: {
            graphqlError,
            operation: operation.operationName,
            variables: operation.variables,
          },
        })
      )
    }

    if (networkError) {
      try {
        // Check if error response is JSON
        JSON.parse(networkError.bodyText)
      } catch {
        // If not, replace HTML error message because it is not readable.
        networkError.message = `Error while carrying out operation: ${operation.operationName}.`
        networkError.bodyText = ''

        console.error(
          `Error while trying to fetch from server. Have a look the resolver/mapper of operation: ${operation.operationName}`
        )
      }

      captureException(new Error(networkError.message), 'graphql', {
        extra: {
          networkError,
          operation: operation.operationName,
          variables: operation.variables,
        },
      })
    }
  })

  // Forward headers link
  const forwardHeadersLink = new ApolloLink((operation, forward) => {
    if (!isBrowser) {
      operation.setContext(({ headers = {} }) => {
        return {
          headers: {
            ...headers,
            ...getForwardedHeaders(),
          },
        }
      })
    }

    return forward(operation)
  })

  // Authentication header link
  const authLink = setContext((_, { headers }) => {
    const token = getToken()

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    }
  })

  // Accept language link
  const acceptLanguageLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => {
      return {
        headers: {
          ...headers,
          'Accept-Language': isBrowser
            ? getLocaleFromHostname(window.location.hostname)
            : getLanguage(),
        },
      }
    })

    return forward(operation)
  })

  // Handle unauthorized requests
  const unauthorizedLink = onError(({ networkError, operation, forward }) => {
    if (networkError) {
      const { statusCode } = networkError
      if (statusCode === 401) {
        const headers = operation.getContext().headers
        if (isBrowser) {
          cookies.remove(TOKEN_COOKIE_NAME)
        }

        operation.setContext({
          headers: {
            ...headers,
            Authorization: '',
          },
        })
      }
    }
    return forward(operation)
  })

  // Retry request if erros
  const retryLink = new RetryLink({
    delay: {
      initial: 1000,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 2,
      retryIf: error => {
        if (dev) return false
        if (error && error.statusCode === 401) return false
        return !!error
      },
    },
  })

  const cache = createApolloCache(initialState)

  const links = [
    authLink,
    acceptLanguageLink,
    errorHandler,
    unauthorizedLink,
    retryLink,
    forwardHeadersLink,
    httpLink,
  ]

  return new ApolloClient({
    connectToDevTools: dev && isBrowser,
    ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
    link: ApolloLink.from(links),
    cache,
  })
}
